import React, { useState, useEffect } from "react";
import { Modal, Input, Button, Alert } from "antd";
import axios from "axios";

const CustomDomainsModal = ({
  visible,
  onClose,
  userId,
  data,
  refetch,
  username,
  warning,
  domainRefetch,
}: any) => {
  const [domain, setDomain] = useState(data?.custom_domain || "");
  const [status, setStatus] = useState<string>("idle");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [nameServers, setNameServers] = useState<string[] | null>(null);

  const creatorSlug = data?.creatorSlug || username;

  useEffect(() => {
    if (warning && data?.name_servers && !nameServers) {
      setNameServers(data.name_servers);
    }
  }, [warning, data, nameServers]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(
        "https://dev.thesearchbubble.com/connect-domain",
        {
          domain,
          creatorSlug,
          user_id: userId,
        }
      );
      if (res.data.error) {
        setStatus(res.data.error);
        setNameServers(null);
        setLoading(false);
        return;
      }
      if (res.data.name_servers) {
        setNameServers(res.data.name_servers);
        setStatus("success");
      } else {
        setStatus("Unknown response from server.");
        setNameServers(null);
      }
      setLoading(false);
      refetch?.();
      domainRefetch?.();
    } catch (e: any) {
      setStatus("Error: " + e.message);
      setNameServers(null);
      setLoading(false);
    }
  };

  const handleDone = async () => {
    try {
      const res = await axios.post(
        "https://dev.thesearchbubble.com/check-domain-status",
        { domain: domain, user_id: userId, creatorSlug: username }
      );

      if (res.data.success) {
        setStatus("✅ Worker successfully attached to domain.");
      } else {
        setStatus("❌ Failed: " + (res.data.message || "Unknown error"));
      }
    } catch (err: any) {
      console.error(err);
      setStatus("❌ Error: " + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
    setDomain("");
    setStatus("idle");
    setNameServers(null);
    onClose();
  };

  return (
    <Modal
      title="Your Custom Domain Setup"
      open={visible}
      onCancel={onClose}
      destroyOnClose
      getContainer={false}
      focusTriggerAfterClose={false}
      footer={
        warning ? (
          <Button type="primary" loading={loading} onClick={handleDone}>
            Done
          </Button>
        ) : nameServers ? (
          <Button type="primary" onClick={onClose}>
            Confirm
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="primary" loading={loading} onClick={handleSave}>
              Save Domain
            </Button>
          </>
        )
      }
    >
      {/* {warning ? (
        <div style={{ marginTop: 24 }}>
          <Input
            placeholder="yourdomain.com"
            value={data?.domain}
            disabled={true}
            style={{ marginBottom: 12 }}
          />
          <Alert
            message={
              <div>
                <div>
                  <b>Almost there! Let's verify your domain setup:</b>
                </div>
                <div style={{ marginTop: 12 }}>
                  We'll check if your domain is properly connected. This might
                  take some time after you've updated your nameservers.
                </div>
                <div style={{ marginTop: 12 }}>
                  <b>If you haven't updated your nameservers yet:</b>
                  <ul style={{ margin: "12px 0" }}>
                    {(data?.name_servers || []).map(
                      (ns: string, idx: number) => (
                        <li key={idx} style={{ fontFamily: "monospace" }}>
                          {ns}
                        </li>
                      )
                    )}
                  </ul>
                  <div style={{ marginTop: 8 }}>
                    Please update these at your domain provider (like GoDaddy,
                    Namecheap, etc.) before clicking Done.
                  </div>
                </div>
              </div>
            }
            type="warning"
            showIcon
          />
        </div>
      ) : ( */}
        <>
          <p style={{ fontSize: "14px", lineHeight: "1.5" }}>
            Want to use your own domain? Great choice! Here's what we'll do:
            <ol style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Enter your domain below (like yourdomain.com)</li>
              <li>
                We'll provide you with special settings to update at your domain
                provider
              </li>
              <li>
                After updating, we'll verify everything is working correctly
              </li>
            </ol>
          </p>
          <Input
            placeholder="yourdomain.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            disabled={loading || !!nameServers}
            style={{ marginBottom: 12 }}
          />

          {status && status !== "idle" && !nameServers && (
            <Alert
              message={status}
              type={status.startsWith("Error") ? "error" : "info"}
              showIcon
              style={{ marginTop: 16, whiteSpace: "pre-line" }}
            />
          )}

          {nameServers && (
            <div style={{ marginTop: 24 }}>
              <Alert
                message={
                  <div>
                    <div>
                      <b>
                        Great! Now we need to connect your domain to our
                        service.
                      </b>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <b>Step 1:</b> Go to your domain provider (like GoDaddy,
                      Namecheap, etc.)
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <b>Step 2:</b> Look for "Nameservers" or "DNS Settings"
                      and update them to these values:
                    </div>
                    <ul style={{ margin: "12px 0", paddingLeft: 20 }}>
                      {nameServers.map((ns, idx) => (
                        <li
                          key={idx}
                          style={{ fontFamily: "monospace", marginBottom: 4 }}
                        >
                          {ns}
                        </li>
                      ))}
                    </ul>
                    <div style={{ marginTop: 12 }}>
                      <b>Step 3:</b> Click "Confirm" below. We'll keep your
                      settings saved.
                    </div>
                    <div style={{ marginTop: 8, color: "#666" }}>
                      <i>
                        Note: It can take up to 24 hours for these changes to
                        take effect. You can come back later to verify the
                        connection.
                      </i>
                    </div>
                  </div>
                }
                type="info"
                showIcon
              />
            </div>
          )}
        </>
      {/* // )} */}
    </Modal>
  );
};

export default CustomDomainsModal;
